import { apiClient, API_PRX } from '@indipia/shared';
import type { AssignedMember } from '@indipia/shared-features';

export interface SiteSummary {
  activeCount: number;
  monthlyDoneCount: number;
}

export interface SiteItem {
  siteId: number;
  customerName: string;
  siteName: string;
  siteAddress: string;
  siteStatus: string;
  siteStatusName?: string;
  lastAccessDtm: string;
  createdAt: string;
  /** sort=last_access 전용 — TX_ACTIVITY_LOG 활동 메타. 다른 정렬에선 null */
  activityTypeCode?: string | null;
  activityAgentId?: number | null;
  activityOccurredDtm?: string | null;
}

// ── E-1 현장 목록 ────────────────────────────────────────────────────────────

export interface SiteListItem {
  siteId: number;
  customerId: number;
  customerName: string;
  customerMobile: string;
  compId: number;
  siteName: string;
  siteAddress: string;
  bldgId: number | null;
  bldgName: string;
  addressDong: string;
  addressHo: string;
  /** 생성 시 서버가 사업자 단위로 순환 배정한 hex 색상. 이 필드 도입 이전 생성 현장은 null */
  siteColor: string | null;
  siteStatus: string;
  siteStatusName?: string;
  planStatus: string;
  lastAccessDtm: string | null;
  createdAt: string;
  /** 현장 담당자·관리자 — 배정 순서대로 (2026-10-12 개선사항 D) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

export interface SiteListParams {
  siteStatus?: string;
  site_status_not?: string;
  keyword?: string;
  dateFrom?: string;
  dateTo?: string;
  sort?: string;
  limit?: number;
  offset?: number;
  /** sort=last_access 전용. true=같은 현장 중복 제거(현장당 최신 활동 1건, 기본값) */
  dedupeSite?: boolean;
  /** 담당자·관리자 조건 — 선택한 회원 중 누구라도 담당자 또는 관리자인 현장(빈 배열 = 전체) */
  memberIds?: number[];
}

export async function fetchSiteList(
  params: SiteListParams = {},
): Promise<{ items: SiteListItem[]; total: number }> {
  const { memberIds, ...rest } = params;
  // 서버는 CSV 를 받는다(memberIds=1,2). 비어 있으면 보내지 않는다(= 전체)
  const query = memberIds?.length ? { ...rest, memberIds: memberIds.join(',') } : rest;
  const res = await apiClient.get(`${API_PRX.CUSTOMER}/sites`, { params: query });
  const d = res.data.data;
  return { items: d?.items ?? [], total: d?.total ?? 0 };
}

export async function updateSiteStatusDone(siteId: number): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}/status`, {
    siteStatus: 'DONE',
  });
}

/** 현장 종료 해제 — 거래완료(DONE)를 종료 직전 상태로 되돌린다(이력 없으면 진행중). 거래완료가 아니면 409 SITE_NOT_DONE */
export async function reopenSite(siteId: number): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}/status/reopen`);
}

/** 현장별 활동 에이전트 (TX_ACTIVITY_LOG distinct AGENT_ID, 플랫폼(AGENT_ID=0) 제외) */
export interface SiteActivityAgents {
  siteId: number;
  agentIds: number[];
}

/** GET /indipia/activity-logs/site-agents — siteId별 활동 에이전트 목록 조회 */
export async function fetchSiteActivityAgents(
  siteIds: number[],
): Promise<SiteActivityAgents[]> {
  if (siteIds.length === 0) return [];
  const res = await apiClient.get(`${API_PRX.INDIPIA}/activity-logs/site-agents`, {
    params: { siteIds: siteIds.join(',') },
  });
  return (res.data.data ?? []) as SiteActivityAgents[];
}

/** 스튜디오 홈 현장카드 "최근N일 현장보고" 1건 — TX_ACTIVITY_LOG 원본 */
export interface SiteActivityItem {
  siteId: number;
  activityTypeCode: string;
  occurredDtm: string;
  targetType: string | null;
  targetId: number | null;
  /** 클릭 시 이동할 웹 화면/메뉴 코드 (TM_MENU.MENU_CODE). 발행자가 안 보내면 null */
  webMenuCode: string | null;
  /** 클릭 시 이동할 앱 화면/메뉴 코드. webMenuCode가 null인데 이 값만 있으면 "앱 전용 화면" 안내 대상 */
  appMenuCode: string | null;
  /** 표시용 문구(activityPayload.text 그대로). "이슈사항" 포함 여부로 FE가 강조표시 하드코딩 매칭 */
  text: string | null;
}

/**
 * GET /indipia/activity-logs/site — siteId 다건+agentId+기간 조건 현장 활동(현장보고) 배치 조회.
 * limit은 요청한 siteIds 전체를 합친 총 건수 상한이다(현장별 상한 아님) — siteIds 개수에 맞춰
 * 여유 있게 넘길 것(기본값 100이면 대체로 충분).
 */
export async function fetchSiteActivities(
  siteIds: number[],
  agentId: number,
  opts: { sinceDays?: number; limit?: number } = {},
): Promise<SiteActivityItem[]> {
  if (siteIds.length === 0) return [];
  const res = await apiClient.get(`${API_PRX.INDIPIA}/activity-logs/site`, {
    params: {
      siteIds: siteIds.join(','),
      agentId,
      sinceDays: opts.sinceDays,
      limit: opts.limit,
    },
  });
  return (res.data.data ?? []) as SiteActivityItem[];
}


// ── E-2 현장 상세 ─────────────────────────────────────────────────────────────

export interface SiteDetailFull {
  siteId: number;
  customerId: number;
  customerName: string;
  customerMobile: string;
  compId: number;
  siteName: string;
  siteAddress: string;
  addressDtl: string | null;
  bldgId: number | null;
  bldgName: string | null;
  addressDong: string | null;
  addressHo: string | null;
  siteMemo: string | null;
  sitePyeong: number | null;
  squareMeters: number | null;
  gpsLat: number | null;
  gpsLng: number | null;
  siteStatus: string;
  siteStatusName?: string;
  planStatus: string;
  floorPlanId: number | null;
  lastAccessDtm: string | null;
  createdAt: string;
  /** 현장 담당자·관리자 — 배정 순서대로 (D-2) */
  chargeMembers?: AssignedMember[];
  managerMembers?: AssignedMember[];
}

export async function fetchSiteDetail(siteId: number): Promise<SiteDetailFull> {
  const res = await apiClient.get(`${API_PRX.CUSTOMER}/sites/${siteId}`);
  return res.data.data as SiteDetailFull;
}

export async function updateSiteInfo(
  siteId: number,
  payload: Partial<{
    customerName: string;
    customerMobile: string;
    addressDtl: string;
    bldgId: number;
    bldgName: string;
    addressDong: string;
    addressHo: string;
    siteMemo: string;
    floorPlanId: number;
    gpsLat: number;
    gpsLng: number;
    /** 배열이면 전체 교체(담당자 1명 이상), 미전송이면 변경 없음 (D-2 · C-6) */
    chargeMemberIds: number[];
    managerMemberIds: number[];
    /** 알림받기를 켠 현장 관리자 — 미전송 = 변경 없음, 배열 = 그 목록만 'Y'·나머지 관리자 'N' (20261015) */
    managerNotiMemberIds: number[];
  }>,
): Promise<void> {
  await apiClient.patch(`${API_PRX.CUSTOMER}/sites/${siteId}`, payload);
}

/** GET /floorplan/indipia/plans/{planId} — sizeType(A/B/C…)만 반환 */
export async function fetchFloorPlanSizeType(planId: number): Promise<string> {
  const res = await apiClient.get(`/floorplan/indipia/plans/${planId}`);
  return (res.data.data?.sizeType ?? '') as string;
}

// ── 홈/공통 ──────────────────────────────────────────────────────────────────

/** 현장 요약 통계 (진행 중 수 / 이번 달 완료 수) */
export async function fetchSitesSummary(): Promise<SiteSummary> {
  const res = await apiClient.get(`${API_PRX.CUSTOMER}/sites/summary`);
  return res.data.data as SiteSummary;
}

/** 최근 활동(TX_ACTIVITY_LOG) 기준 현장 N개 (홈 화면용). dedupeSite=true — 같은 현장 중복 제거 */
export async function fetchRecentSites(limit = 3): Promise<SiteItem[]> {
  const res = await apiClient.get(`${API_PRX.CUSTOMER}/sites`, {
    params: { sort: 'last_access', limit, site_status_not: 'DONE', dedupeSite: true },
  });
  return (res.data.data?.items ?? []) as SiteItem[];
}
